import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useWindowWidth from '../hooks/useWindowWidth';
import AppLayout from '../components/layout/AppLayout';
import PerturbationPanel from '../components/perturbation/PerturbationPanel';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const DEFAULT_PERTURBATION = {
  voice_quality: 'none',
  speech_rate: 'none',
  pause_length: 'none',
  background_noise: 'none',
  channel_quality: 'studio',
  pitch_variation: 'normal',
  vocal_tremor: 'none',
  breathiness: 'none',
  hypophonia: 'none',
  hypernasality: 'none',
  pitch_instability: 'none',
  articulatory_imprecision: 'none',
  irregular_rate: 'none',
  strained_quality: 'none',
};

function isPerturbationActive(config) {
  return (
    config.voice_quality !== 'none' ||
    config.speech_rate !== 'none' ||
    config.pause_length !== 'none' ||
    config.background_noise !== 'none' ||
    config.channel_quality !== 'studio' ||
    config.pitch_variation !== 'normal' ||
    config.vocal_tremor !== 'none' ||
    config.breathiness !== 'none' ||
    config.hypophonia !== 'none' ||
    config.hypernasality !== 'none' ||
    config.pitch_instability !== 'none' ||
    config.articulatory_imprecision !== 'none' ||
    config.irregular_rate !== 'none' ||
    config.strained_quality !== 'none'
  );
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  sub: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
  backBtn: {
    backgroundColor: '#f3f4f6', color: '#374151', border: 'none',
    borderRadius: '8px', padding: '8px 16px', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer',
  },
  form: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' },
  section: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    height: '100%', boxSizing: 'border-box',
  },
  sectionFull: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    gridColumn: '1 / -1',
  },
  sectionTitle: {
    fontSize: '14px', fontWeight: '700', color: '#0052CC',
    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px',
    paddingBottom: '12px', borderBottom: '1px solid #e5e7eb',
  },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' },
  input: {
    width: '100%', padding: '9px 12px', borderRadius: '8px',
    border: '1px solid #d1d5db', fontSize: '14px', marginBottom: '16px',
    boxSizing: 'border-box', outline: 'none',
  },
  select: {
    width: '100%', padding: '9px 12px', borderRadius: '8px',
    border: '1px solid #d1d5db', fontSize: '14px', marginBottom: '16px',
    boxSizing: 'border-box', outline: 'none', backgroundColor: '#fff',
  },
  textarea: {
    width: '100%', padding: '9px 12px', borderRadius: '8px',
    border: '1px solid #d1d5db', fontSize: '14px', marginBottom: '16px',
    boxSizing: 'border-box', outline: 'none', minHeight: '100px', resize: 'vertical',
  },
  llmAgentGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: '24px' },
  hint: { fontSize: '12px', color: '#9ca3af', marginTop: '-12px', marginBottom: '8px' },
  subLabel: { fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '8px', marginTop: '4px' },
  agentGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
    gap: '8px', marginBottom: '12px',
  },
  agentCard: {
    padding: '10px 12px', borderRadius: '8px',
    border: '2px solid #e5e7eb', cursor: 'pointer',
    color: '#374151', userSelect: 'none',
  },
  agentCardSelected: { border: '2px solid #0052CC', backgroundColor: '#EBF5FF', color: '#0052CC' },
  agentCardName: { fontSize: '13px', fontWeight: '700', marginBottom: '2px' },
  agentCardRole: { fontSize: '11px' },
  divider: { borderTop: '1px solid #e5e7eb', margin: '12px 0' },
  initiatorCard: {
    padding: '10px 14px', borderRadius: '8px',
    border: '2px solid #e5e7eb', cursor: 'pointer',
    color: '#374151', userSelect: 'none', marginBottom: '8px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  initiatorCardActive: { border: '2px solid #16a34a', backgroundColor: '#f0fdf4', color: '#16a34a' },
  initiatorCardDisabled: {
    border: '2px solid #e5e7eb', backgroundColor: '#f9fafb',
    color: '#d1d5db', cursor: 'not-allowed',
  },
  initiatorName: { fontSize: '13px', fontWeight: '700' },
  initiatorBadge: {
    fontSize: '10px', fontWeight: '700', color: '#16a34a',
    backgroundColor: '#dcfce7', padding: '2px 8px', borderRadius: '20px',
  },
  selectionCount: { fontSize: '12px', color: '#9ca3af', marginTop: '4px' },
  outputModeRow: { display: 'flex', gap: '12px', marginBottom: '16px' },
  outputModeBtn: {
    flex: 1, padding: '10px', borderRadius: '8px', border: '2px solid #e5e7eb',
    fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer',
    backgroundColor: '#fff', textAlign: 'center',
  },
  outputModeBtnActive: { border: '2px solid #0052CC', backgroundColor: '#EBF5FF', color: '#0052CC' },
  outputModeBtnLocked: {
    border: '2px solid #d1d5db', backgroundColor: '#f3f4f6', color: '#9ca3af',
    cursor: 'not-allowed',
  },
  outputModeHint: { fontSize: '11px', color: '#d97706', marginBottom: '16px' },
  actions: { gridColumn: '1 / -1', display: 'flex', gap: '12px', justifyContent: 'flex-end', alignItems: 'center' },
  saveBtn: {
    backgroundColor: '#0052CC', color: '#fff', border: 'none',
    borderRadius: '8px', padding: '12px 32px', fontSize: '15px',
    fontWeight: '600', cursor: 'pointer',
  },
  saveBtnDisabled: {
    backgroundColor: '#d1d5db', color: '#9ca3af', border: 'none',
    borderRadius: '8px', padding: '12px 32px', fontSize: '15px',
    fontWeight: '600', cursor: 'not-allowed',
  },
  cancelBtn: {
    backgroundColor: '#f3f4f6', color: '#374151', border: 'none',
    borderRadius: '8px', padding: '12px 24px', fontSize: '15px',
    fontWeight: '600', cursor: 'pointer',
  },
  validationHint: { fontSize: '12px', color: '#9ca3af' },
  error: {
    backgroundColor: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626',
    borderRadius: '8px', padding: '12px 16px', fontSize: '13px',
    marginBottom: '16px', gridColumn: '1 / -1',
  },
  success: {
    backgroundColor: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a',
    borderRadius: '8px', padding: '12px 16px', fontSize: '13px',
    marginBottom: '16px', gridColumn: '1 / -1',
  },
  turnsRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  emptyAgents: { fontSize: '13px', color: '#6b7280', marginTop: '8px' },
  emptyLink: { color: '#0052CC', cursor: 'pointer', fontWeight: '600' },
  turnOrderBtn: {
    flex: 1, padding: '10px 12px', borderRadius: '8px',
    cursor: 'pointer', textAlign: 'center',
    fontSize: '13px', fontWeight: '600',
  },
  hubAgentCard: {
    padding: '8px 12px', borderRadius: '8px', marginBottom: '6px',
    cursor: 'pointer', fontSize: '13px', fontWeight: '500',
  },
};

export default function ScenarioBuilder() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { currentUser, isPro } = useAuth();
  const width = useWindowWidth();
  const isMobile = width < 768;
  const [title, setTitle] = useState('');
  const [context, setContext] = useState('');
  const [language, setLanguage] = useState('english');
  const [maxTurns, setMaxTurns] = useState(10);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [initiatingAgentId, setInitiatingAgentId] = useState('');
  const [turnOrderMode, setTurnOrderMode] = useState('round_robin');
  const [hubAgentId, setHubAgentId] = useState('');
  const [outputMode, setOutputMode] = useState('text');
  const [perturbationConfigs, setPerturbationConfigs] = useState({});
  const [availableAgents, setAvailableAgents] = useState([]);
  const [sttProviders, setSttProviders] = useState([]);
  const [sttProvider, setSttProvider] = useState('deepgram');
  const [sttModel, setSttModel] = useState('nova-2');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [parametricConfigs, setParametricConfigs] = useState({});

  useEffect(() => {
    fetchAgents();
    fetchSttProviders();
    if (isEdit) fetchScenario();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const anyAcousticActive = Object.values(perturbationConfigs).some(isPerturbationActive);
    const anyParametricActive = Object.values(parametricConfigs).some(agentParams => {
      if (!agentParams) return false;
      const PARAMETRIC_DEFAULTS_CHECK = {
        tremolo_freq: 0, tremolo_depth: 0,
        vibrato_freq: 0, vibrato_depth: 0,
        lowpass_freq: 0,
        compressor_threshold: 0, compressor_ratio: 1,
        tempo_min: 1.0, tempo_max: 1.0, max_step: 0.12,
        eq_freq: 3000, eq_gain: 0, eq_width: 2,
        volume: 1.0,
        nasal_boost_freq: 350, nasal_boost_gain: 0, nasal_boost_width: 200, oral_cut_gain: 0,
        stability: 0.75, similarity_boost: 0.75, style: 0,
        speed: 1.0,
        pause_ms: 0,
        snr_db: 35, noise_color: 'pink',
        channel_highpass_freq: 0, channel_lowpass_freq: 20000,
      };
      return Object.entries(agentParams).some(([k, v]) => {
        const def = PARAMETRIC_DEFAULTS_CHECK[k];
        return def !== undefined && v !== def;
      });
    });

    const anyActive = anyAcousticActive || anyParametricActive;

    if (anyActive) {
      setOutputMode('voice_stt');
    } else {
      setOutputMode(prev => prev === 'voice_stt' ? 'text' : prev);
    }
  }, [perturbationConfigs, parametricConfigs]);

  async function fetchAgents() {
    try {
      const res = await fetch(`${API}/api/agents?owner_id=${currentUser.uid}`);
      const data = await res.json();
      setAvailableAgents(Array.isArray(data) ? data : []);
    } catch { }
  }

  async function fetchSttProviders() {
    try {
      const res = await fetch(`${API}/api/stt/providers`);
      const data = await res.json();
      setSttProviders(Array.isArray(data) ? data : []);
    } catch { }
  }

  async function fetchScenario() {
    try {
      const res = await fetch(`${API}/api/scenarios/${id}`);
      const data = await res.json();
      setTitle(data.title || '');
      setContext(data.context || '');
      setLanguage(data.language || 'english');
      setMaxTurns(data.max_turns || 10);
      setSelectedAgents(data.agent_ids || []);
      setInitiatingAgentId(data.initiating_agent_id || '');
      setTurnOrderMode(data.turn_order_mode || 'round_robin');
      setHubAgentId(data.hub_agent_id || '');
      setOutputMode(data.output_mode || 'text');
      setSttProvider(data.stt_provider || 'whisper');
      setSttModel(data.stt_model || 'whisper-1');
      setPerturbationConfigs(data.perturbation_config?.agents || {});
      setParametricConfigs(data.parametric_config?.agents || {});
    } catch { setError('Failed to load scenario.'); }
  }

  function toggleAgentSelect(agentId) {
    setSelectedAgents(prev => {
      if (prev.includes(agentId)) {
        if (initiatingAgentId === agentId) setInitiatingAgentId('');
        if (hubAgentId === agentId) setHubAgentId('');
        return prev.filter(a => a !== agentId);
      }
      const maxAgents = isPro ? 6 : 2;
      if (prev.length >= maxAgents) return prev;
      return [...prev, agentId];
    });
  }

  function toggleInitiator(agentId) {
    setInitiatingAgentId(prev => prev === agentId ? '' : agentId);
  }

  function updatePerturbation(agentId, config) {
    setPerturbationConfigs(prev => ({ ...prev, [agentId]: config }));
  }

  function updateLinguistic(agentId, config) {
  }

  function updateParametric(agentId, config) {
  setParametricConfigs(prev => ({ ...prev, [agentId]: config }));
}

  const selectedAgentObjects = availableAgents.filter(a => selectedAgents.includes(a.id));
  const anyPerturbationActive = Object.values(perturbationConfigs).some(isPerturbationActive);

  const PARAMETRIC_DEFAULTS_RENDER = {
    tremolo_freq: 0, tremolo_depth: 0, vibrato_freq: 0, vibrato_depth: 0,
    lowpass_freq: 0, compressor_threshold: 0, compressor_ratio: 1,
    tempo_min: 1.0, tempo_max: 1.0, max_step: 0.12,
    eq_freq: 3000, eq_gain: 0, eq_width: 2, volume: 1.0,
    nasal_boost_freq: 350, nasal_boost_gain: 0, nasal_boost_width: 200, oral_cut_gain: 0,
    stability: 0.75, similarity_boost: 0.75, style: 0,
    speed: 1.0, pause_ms: 0, snr_db: 35, noise_color: 'pink',
    channel_highpass_freq: 0, channel_lowpass_freq: 20000,
  };

  const anyParametricActive = Object.values(parametricConfigs).some(agentParams => {
    if (!agentParams) return false;
    return Object.entries(agentParams).some(([k, v]) => {
      const def = PARAMETRIC_DEFAULTS_RENDER[k];
      return def !== undefined && v !== def;
    });
  });

  const anyAnyPerturbationActive = anyPerturbationActive || anyParametricActive;

  const isFormValid = (
    title.trim() !== '' &&
    context.trim() !== '' &&
    selectedAgents.length >= 2 &&
    initiatingAgentId !== '' &&
    maxTurns >= 2
  );

  function getMissingFields() {
    const missing = [];
    if (!title.trim()) missing.push('title');
    if (!context.trim()) missing.push('context');
    if (selectedAgents.length < 2) missing.push('at least 2 agents');
    if (!initiatingAgentId) missing.push('conversation starter');
    if (maxTurns < 2) missing.push('max turns ≥ 2');
    return missing;
  }

  function getMissingFields() {
    const missing = [];
    if (!title.trim()) missing.push('title');
    if (!context.trim()) missing.push('context');
    if (selectedAgents.length < 2) missing.push('at least 2 agents');
    if (!initiatingAgentId) missing.push('conversation starter');
    if (maxTurns < 2) missing.push('max turns ≥ 2');
    return missing;
  }

  async function handleSave() {
    if (selectedAgents.length > 6) { setError('Maximum 6 agents allowed.'); return; }
    if (!isFormValid) { setError(`Missing required fields: ${getMissingFields().join(', ')}.`); return; }

    setLoading(true);
    setError('');
    setSuccess('');

    const payload = {
      owner_id: currentUser.uid,
      title, context, language,
      agent_count: selectedAgents.length,
      max_turns: maxTurns,
      agent_ids: selectedAgents,
      initiating_agent_id: initiatingAgentId,
      turn_order_mode: turnOrderMode,
      hub_agent_id: hubAgentId || '',
      output_mode: outputMode,
      stt_provider: sttProvider,
      stt_model: sttModel,
      perturbation_config: {
        output_mode: outputMode,
        agents: perturbationConfigs,
      },
      parametric_config: {
        agents: parametricConfigs,
      },
    };

    try {
      const url = isEdit ? `${API}/api/scenarios/${id}` : `${API}/api/scenarios`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Save failed');
      setSuccess(isEdit ? 'Scenario updated.' : 'Scenario created.');
      setTimeout(() => navigate('/scenarios'), 1000);
    } catch {
      setError('Failed to save scenario.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AppLayout>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>{isEdit ? 'Edit Scenario' : 'New Scenario'}</div>
          <div style={styles.sub}>Define simulation context, agents, and evaluation targets</div>
        </div>
        <button style={styles.backBtn} onClick={() => navigate('/scenarios')}>← Back</button>
      </div>

      <div style={{ ...styles.form, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}

        {/* Scenario Details */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Scenario Details</div>
          <label style={styles.label}>Title *</label>
          <input
            style={styles.input} value={title}
            onChange={e => setTitle(e.target.value.slice(0, 150))}
            placeholder="e.g. Healthcare Consultation"
            maxLength={150}
          />
          <div style={{ fontSize: '11px', color: title.length > 120 ? '#dc2626' : '#9ca3af', textAlign: 'right', marginBottom: '8px' }}>{title.length}/150</div>
          <label style={styles.label}>Context *</label>
          <textarea
            style={styles.textarea} value={context}
            onChange={e => setContext(e.target.value.slice(0, 1000))}
            placeholder="Describe the shared situation both agents are operating in."
            maxLength={1000}
          />
          <div style={{ fontSize: '11px', color: context.length > 850 ? '#dc2626' : '#9ca3af', textAlign: 'right', marginBottom: '8px' }}>{context.length}/1000</div>
  
          <label style={styles.label}>Language</label>
          <select style={styles.select} value={language} onChange={e => setLanguage(e.target.value)}>
            <option value="english">English</option>
            <option value="spanish">Spanish</option>
            <option value="french">French</option>
            <option value="german">German</option>
            <option value="italian">Italian</option>
            <option value="portuguese">Portuguese</option>
            <option value="polish">Polish</option>
            <option value="hindi">Hindi</option>
            <option value="japanese">Japanese</option>
            <option value="mandarin">Chinese (Mandarin)</option>
            <option value="korean">Korean</option>
            <option value="arabic">Arabic</option>
            <option value="dutch">Dutch</option>
            <option value="turkish">Turkish</option>
            <option value="swedish">Swedish</option>
            <option value="indonesian">Indonesian</option>
            <option value="filipino">Filipino</option>
            <option value="ukrainian">Ukrainian</option>
            <option value="greek">Greek</option>
            <option value="czech">Czech</option>
            <option value="finnish">Finnish</option>
            <option value="romanian">Romanian</option>
            <option value="danish">Danish</option>
            <option value="bulgarian">Bulgarian</option>
            <option value="malay">Malay</option>
            <option value="slovak">Slovak</option>
            <option value="croatian">Croatian</option>
            <option value="tamil">Tamil</option>
            <option value="norwegian">Norwegian</option>
            <option value="hungarian">Hungarian</option>
            <option value="vietnamese">Vietnamese</option>
            <option value="russian">Russian</option>
          </select>
          <div style={{ ...styles.turnsRow, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
            <div>
              <label style={styles.label}>Max Turns *</label>
              <input
                style={styles.input} type="number" min="2" max={isPro ? 100 : 6}
                value={maxTurns}
                onChange={e => setMaxTurns(e.target.value)}
                onBlur={e => {
                  const val = parseInt(e.target.value) || 2;
                  const clamped = isPro ? Math.max(2, val) : Math.min(Math.max(2, val), 6);
                  setMaxTurns(clamped);
                }}
              />
              {!isPro && (
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                  Max 6 turns on Standard plan. <a href="mailto:info@converstation.ai" style={{ color: '#0052CC' }}>Upgrade to Professional</a> for unlimited turns.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Agents */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Agents</div>
          <div style={styles.llmAgentGrid}>
            <div>
              <div style={styles.subLabel}>
                STEP 1 — Select 2 to 6 Agents ({selectedAgents.length}/6)
              </div>
              {availableAgents.length === 0 ? (
                <div style={styles.emptyAgents}>
                  No agents yet.{' '}
                  <span style={styles.emptyLink} onClick={() => navigate('/agents/new')}>Create one →</span>
                </div>
              ) : (
                <>
                  <select
                    style={styles.select} value=""
                    onChange={e => { if (e.target.value && selectedAgents.length < (isPro ? 6 : 2)) toggleAgentSelect(e.target.value); }}
                  >
                    <option value="">Select an agent...</option>
                    {availableAgents
                      .filter(a => !selectedAgents.includes(a.id))
                      .map(agent => (
                        <option key={agent.id} value={agent.id}>
                          {agent.name}{agent.role ? ` — ${agent.role}` : ''}
                        </option>
                      ))}
                  </select>
                  
                  {!isPro && (
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                      Max 2 agents on Standard plan. <a href="mailto:info@converstation.ai" style={{ color: '#0052CC' }}>Upgrade to Professional</a> for up to 6 agents.
                    </div>
                  )}
                  {selectedAgents.length > 0 && (
                    <div style={styles.agentGrid}>
                      {selectedAgentObjects.map(agent => (
                        <div
                          key={agent.id}
                          style={{ ...styles.agentCard, ...styles.agentCardSelected }}
                          onClick={() => toggleAgentSelect(agent.id)}
                        >
                          <div style={styles.agentCardName}>{agent.name}</div>
                          <div style={styles.agentCardRole}>{agent.role || 'No role'} · ✕</div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {selectedAgents.length >= 2 && (
                <>
                  <div style={styles.divider} />
                  <div style={styles.subLabel}>STEP 2 — Who starts the conversation?</div>
                  {selectedAgentObjects.map(agent => {
                    const isInitiator = initiatingAgentId === agent.id;
                    const isBlocked = initiatingAgentId !== '' && !isInitiator;
                    const cardStyle = isInitiator
                      ? { ...styles.initiatorCard, ...styles.initiatorCardActive }
                      : isBlocked
                        ? { ...styles.initiatorCard, ...styles.initiatorCardDisabled }
                        : styles.initiatorCard;
                    return (
                      <div key={agent.id} style={cardStyle}
                        onClick={() => !isBlocked && toggleInitiator(agent.id)}>
                        <div style={styles.initiatorName}>{agent.name}</div>
                        {isInitiator && <div style={styles.initiatorBadge}>▶ Starts conversation</div>}
                      </div>
                    );
                  })}

                  {/* Turn order — only for 3+ agents */}
                  {selectedAgents.length >= 3 && (
                    <>
                      <div style={styles.divider} />
                      <div style={styles.subLabel}>STEP 3 — Turn Order</div>
                      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                        {['round_robin', 'hub_spoke'].map(mode => (
                          <div
                            key={mode}
                            style={{
                              ...styles.turnOrderBtn,
                              border: turnOrderMode === mode ? '2px solid #0052CC' : '2px solid #e5e7eb',
                              backgroundColor: turnOrderMode === mode ? '#EBF5FF' : '#fff',
                              color: turnOrderMode === mode ? '#0052CC' : '#374151',
                            }}
                            onClick={() => setTurnOrderMode(mode)}
                          >
                            {mode === 'round_robin' ? '🔄 Round Robin' : '⭐ Hub & Spoke'}
                          </div>
                        ))}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                        {turnOrderMode === 'round_robin'
                          ? `Order: ${selectedAgentObjects.map(a => a.name).join(' → ')} → repeat`
                          : "Hub agent speaks after every other agent's turn"}
                      </div>
                      {turnOrderMode === 'hub_spoke' && (
                        <>
                          <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                            Select Hub Agent
                          </div>
                          {selectedAgentObjects.map(agent => (
                            <div
                              key={agent.id}
                              style={{
                                ...styles.hubAgentCard,
                                border: hubAgentId === agent.id ? '2px solid #0052CC' : '2px solid #e5e7eb',
                                backgroundColor: hubAgentId === agent.id ? '#EBF5FF' : '#fff',
                                color: hubAgentId === agent.id ? '#0052CC' : '#374151',
                              }}
                              onClick={() => setHubAgentId(agent.id)}
                            >
                              {agent.name}{agent.role ? ` · ${agent.role}` : ''}
                              {hubAgentId === agent.id && ' ⭐'}
                            </div>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Output Mode + Perturbations */}
        <div style={{ ...styles.sectionFull, gridColumn: isMobile ? '1' : '1 / -1' }}>
          <div style={styles.sectionTitle}>Output Mode & Acoustic Perturbations</div>
          <label style={styles.label}>Modality</label>
          <div style={{ ...styles.outputModeRow, flexDirection: isMobile ? 'column' : 'row' }}>
            {[
              { id: 'text', label: '📝 Transcript Only' },
              { id: 'voice', label: '🔊 Transcript + Voice' },
              { id: 'voice_stt', label: '🔊🎙 Voice + STT' },
            ].map(mode => {
              const isLocked = anyAnyPerturbationActive && (mode.id === 'text' || mode.id === 'voice');
              const isActive = outputMode === mode.id;
              const btnStyle = isLocked
                ? { ...styles.outputModeBtn, ...styles.outputModeBtnLocked }
                : isActive
                  ? { ...styles.outputModeBtn, ...styles.outputModeBtnActive }
                  : styles.outputModeBtn;
              return (
                <div key={mode.id} style={btnStyle} onClick={() => { if (!isLocked) setOutputMode(mode.id); }}>
                  {mode.label}{isLocked && ' (locked)'}
                </div>
              );
            })}
          </div>
          {anyAnyPerturbationActive && (
            <div style={styles.outputModeHint}>
              ⚡ Perturbation active — Transcript Only and Voice only are unavailable.
            </div>
          )}

          {outputMode === 'voice_stt' && (
            <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#0052CC', marginBottom: '12px' }}>STT Configuration</div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={styles.label}>STT Provider</label>
                  <select
                    style={styles.select}
                    value={sttProvider}
                    onChange={e => {
                      setSttProvider(e.target.value);
                      const first = sttProviders.find(p => p.id === e.target.value)?.models[0]?.id || '';
                      setSttModel(first);
                    }}
                  >
                    {sttProviders.map(p => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.label}>STT Model</label>
                  <select style={styles.select} value={sttModel} onChange={e => setSttModel(e.target.value)}>
                    {(sttProviders.find(p => p.id === sttProvider)?.models || []).map(m => (
                      <option key={m.id} value={m.id}>{m.label} — {m.description}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {selectedAgentObjects.length > 0 ? (
            selectedAgentObjects.map(agent => (
              <PerturbationPanel
                key={agent.id}
                agentName={agent.name}
                config={perturbationConfigs[agent.id] || DEFAULT_PERTURBATION}
                onChange={config => updatePerturbation(agent.id, config)}
                parametricConfig={parametricConfigs[agent.id] || {}}
                onParametricChange={config => updateParametric(agent.id, config)}
                isPro={isPro}
              />
            ))
          ) : (
            <div style={{ fontSize: '13px', color: '#9ca3af' }}>
              Select agents above to configure per-agent perturbations.
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ ...styles.actions, flexDirection: isMobile ? 'column' : 'row', gridColumn: '1 / -1' }}>
          {!isFormValid && (
            <div style={styles.validationHint}>Missing: {getMissingFields().join(', ')}</div>
          )}
          <button style={styles.cancelBtn} onClick={() => navigate('/scenarios')}>Cancel</button>
          <button
            style={isFormValid ? styles.saveBtn : styles.saveBtnDisabled}
            onClick={handleSave}
            disabled={!isFormValid || loading}
          >
            {loading ? 'Saving...' : isEdit ? 'Update Scenario' : 'Create Scenario'}
          </button>
        </div>
      </div>
    </AppLayout>
  );
}
